import type { MutableRefObject } from 'react';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';

import type { DashboardDataModel } from '../../../shared/dashboard-data';
import { DEFAULT_POLYGON_STYLE } from '../../utils/map-constants';
import { buildTooltipHtml } from '../../utils/map-tooltip';
import type { PolygonLayer } from '../../utils/types';

interface DashboardMapProps {
  featureCollection: DashboardDataModel['featureCollection'];
  mapCenter: [number, number];
  tdtToken: string;
  polygonLayerMapRef: MutableRefObject<Map<number, PolygonLayer>>;
  onPolygonToggle: (objectId: number) => void;
}

function ZoomPad() {
  const map = useMap();

  return (
    <div className="map-zoom-pad">
      <button type="button" className="map-zoom-btn" onClick={() => map.zoomIn()} aria-label="放大">
        +
      </button>
      <button type="button" className="map-zoom-btn" onClick={() => map.zoomOut()} aria-label="缩小">
        -
      </button>
    </div>
  );
}

export function DashboardMap({
  featureCollection,
  mapCenter,
  tdtToken,
  polygonLayerMapRef,
  onPolygonToggle,
}: DashboardMapProps) {
  return (
    <div className="dashboard-map-layer">
      <MapContainer
        className="dashboard-map"
        center={mapCenter}
        zoom={15}
        zoomControl={false}
        attributionControl={false}
        keyboard={false}
        minZoom={11}
        maxZoom={18}
      >
        <TileLayer
          url={`https://t{s}.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tdtToken}`}
          subdomains={['0', '1', '2', '3', '4', '5', '6', '7']}
        />
        <TileLayer
          url={`https://t{s}.tianditu.gov.cn/cia_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cia&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tdtToken}`}
          subdomains={['0', '1', '2', '3', '4', '5', '6', '7']}
        />

        <GeoJSON
          data={featureCollection}
          style={{
            className: 'greenland-polygon',
            ...DEFAULT_POLYGON_STYLE,
          }}
          onEachFeature={(feature, layer) => {
            const properties = feature.properties;
            const objectId = Number(feature.properties?.OBJECTID ?? 0);
            const polygonLayer = layer as PolygonLayer;

            if (objectId > 0) {
              polygonLayerMapRef.current.set(objectId, polygonLayer);
            }

            layer.bindPopup(buildTooltipHtml(properties), {
              className: 'leaflet-rich-popup',
              closeButton: false,
              autoPan: true,
            });

            layer.on({
              click: () => {
                if (objectId <= 0) {
                  return;
                }
                onPolygonToggle(objectId);
              },
              popupopen: (event) => {
                const target = event.target as PolygonLayer;
                if (typeof target.bringToFront === 'function') {
                  target.bringToFront();
                }
              },
            });
          }}
        />
        <ZoomPad />
      </MapContainer>
    </div>
  );
}
